import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {ThunkDispatch} from 'redux-thunk';
import {AnyAction} from 'redux';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faThumbsUp, faThumbsDown} from '@fortawesome/free-solid-svg-icons';

import {Article, ArticleService} from '../Services/ArticleService';
import {AppState} from '../Store';
import {getVoteCreator, setVote, setArticle} from '../Store/Articles/Actions';

interface RatingWidgetProps {
  article?: Article;
  articleService: ArticleService;
  votes: Record<number, boolean | undefined>;
  getVote: (id: number) => Promise<void>;
  setVote: (id: number, vote?: boolean) => void;
  setArticle: (article: Article) => void;
}

function RatingWidget({
  article,
  votes,
  articleService,
  getVote,
  setVote,
  setArticle,
}: RatingWidgetProps) {
  const articleId = article?.id;

  useEffect(() => {
    if (articleId) {
      getVote(articleId);
    }
  }, [articleId, getVote]);

  if (article === undefined) {
    return <div />;
  }

  const { upvotes, downvotes } = article;
  const upvotePercent = upvotes + downvotes === 0
    ? 0
    : (upvotes / (upvotes + downvotes)) * 100;
  const downvotePercent = upvotes + downvotes === 0
    ? 0
    : 100 - upvotePercent;

  const vote = votes[article.id];

  const castVote = async (upvote: boolean) => {
    const actualVote = upvote !== vote
      ? upvote
      : undefined;

    const response = await articleService.vote(article.id, actualVote);
    setVote(article.id, actualVote);
    if (response.parsedBody) {
      setArticle(response.parsedBody);
    }
  };

  return (
    <div className="sidebar-widget-area">
      <h5 className="title">Ratings</h5>
      <div className="widget-content">
        <div className="d-flex flex-row">
          <FontAwesomeIcon
            className="align-self-start mr-2"
            icon={faThumbsUp}
            onClick={() => castVote(true)}
            style={{ color: vote === true ? '#28a745' : '#000' }}
          />
          <div className="flex-grow-1">
            <div className="progress">
              <div
                className="progress-bar bg-success"
                style={{width: `${upvotePercent}%`}}
              >
                {upvotes > 0 ? upvotes : ''}
              </div>
              <div
                className="progress-bar bg-danger"
                style={{width: `${downvotePercent}%`}}
              >
                {downvotes > 0 ? downvotes : ''}
              </div>
            </div>
          </div>
          <FontAwesomeIcon
            className="align-self-end ml-2"
            icon={faThumbsDown}
            onClick={() => castVote(false)}
            style={{ color: vote === false ? '#dc3545' : '#000' }}
          />
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state: AppState) => ({
  articleService: state.auth.articleService,
  votes: state.articles.votes,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  getVote: (id: number) => dispatch(getVoteCreator(id)),
  setVote: (id: number, vote?: boolean) => dispatch(setVote(id, vote)),
  setArticle: (article: Article) => dispatch(setArticle(article)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RatingWidget);
